import * as React from 'react';
import { Fragment, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, ExternalLink, Moon, Settings2, Sparkles, Sun } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AuthContext } from '../../providers/AuthProvider';
import { useAskAi } from '../../modules/vendor/assistant/AskAiContext';
import { config } from '../hooks/use-settings-api.hook';
import { useThemeMode } from '../../components/design-system/hooks';
import { useNotificationsApi } from '../hooks/use-notifications-api.hook';
import { useNotificationStore } from '../stores/notification.store';
import { NotificationList } from './NotificationList';
import { usePushNotifications } from '../hooks/use-push-notifications.hook';
import { notificationsPath } from '../../modules/notifications/notification-presentation';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../utils/visualizer-link';
import { SupportLauncher } from '../../modules/support/vendor/SupportWidget';
import { StaffSupportLauncher } from '../../modules/support/staff/staff-support.store';
import { PresenceStack } from '../../modules/presence/PresenceStack';
import { EnvironmentBadge } from '../components/environment/EnvironmentBadge';
import { useReportPresencePage } from '../../modules/presence/PresenceProvider';
import { TrialPill } from '../../components/design-system/plan/TrialPill';

/** One step of the trail. `href` makes it a link; the last one never is. */
export interface Crumb {
  label: string;
  href?: string;
}

interface TopNavBarProps {
  title?: string;
  /**
   * Where the page sits, shown at the bar's left.
   *
   * Here rather than at the top of the page body, for three reasons. The bar
   * is `sticky` and the body is not, so in the page content the trail scrolled
   * away exactly when a person was deep enough to want it. The last crumb and
   * the page's own `h1` were the same words eight pixels apart. And the bar's
   * left had held nothing but the sidebar toggle since search moved into the
   * rail.
   */
  breadcrumbs?: Crumb[];
  /**
   * The page's own status, at the right before the tools — e.g. an autosaving
   * page's "Saving…" / "Saved". Pages without one pass nothing.
   */
  status?: React.ReactNode;
}

/**
 * The application bar.
 *
 * Tools only — search, the assistant, notifications, the visualizer link.
 * Where you are is said at the top of the page itself, so nothing here has
 * to change when the route does.
 */
export function TopNavBar({ title, breadcrumbs, status }: TopNavBarProps) {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { isDarkMode, toggleColorMode } = useThemeMode();
  const { isSuperAdmin } = React.useContext(AuthContext);
  const askAi = useAskAi();
  const { pathname } = useLocation();

  // What teammates see as "where" in the presence stack: this page's own trail.
  useReportPresencePage(pathname, breadcrumbs?.length ? breadcrumbs.map((c) => c.label) : title ? [title] : []);

  const notificationsApi = useNotificationsApi();
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const notifItems = useNotificationStore((s) => s.items);
  const notifLoading = useNotificationStore((s) => s.isLoading);
  const [notifOpen, setNotifOpen] = useState(false);
  const push = usePushNotifications();
  // Nothing to offer without a service worker (dev server), without the Push
  // API, or while the API has no VAPID keys.
  const showPushRow = push.status !== 'unsupported' && push.status !== 'unavailable';
  const pushHint = {
    on: 'PUSH.HINT_ON',
    off: 'PUSH.HINT_OFF',
    denied: 'PUSH.HINT_DENIED',
    'ios-install': 'PUSH.HINT_IOS',
  }[push.status];


  /** Marking everything read on open is deliberate: the badge is a nudge, not a queue. */
  const handleNotifOpen = async () => {
    setNotifOpen(true);
    const store = useNotificationStore.getState();
    store.setLoading(true);
    try {
      const res = await notificationsApi.findAll({ page: 1, limit: 20 });
      store.setItems(res.data);
      if (useNotificationStore.getState().unreadCount > 0) {
        await notificationsApi.markAllRead();
        store.markAllReadLocal();
      }
    } catch {
      // Silent — the panel just shows the last known items.
    } finally {
      useNotificationStore.getState().setLoading(false);
    }
  };

  const openVisualizer = () => {
    const url = buildVisualizerUrl({ alias: config.alias, attribution: PREVIEW_LINK_ATTRIBUTION });
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (opened) opened.opener = null;
  };


  return (
    <header data-slot="top-nav" className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b bg-background px-4 md:px-6">
      <SidebarTrigger className="-ms-1" />

      {/* Search lives in the sidebar now, with the command palette behind it. */}

      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="flex-nowrap">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return (
                <Fragment key={`${crumb.label}-${index}`}>
                  {index > 0 && (
                    /*
                     * Everything but the last crumb steps aside on a narrow
                     * screen. The bar already carries four controls on the
                     * right, and a deep trail would push into them — where the
                     * one crumb worth keeping is the one saying where you are.
                     */
                    <BreadcrumbSeparator className="hidden md:block" />
                  )}
                  <BreadcrumbItem className={cn('min-w-0', !isLast && 'hidden md:inline-flex')}>
                    {isLast || !crumb.href ? (
                      <BreadcrumbPage className={cn('truncate', !isLast && 'font-normal text-muted-foreground')}>
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href} className="truncate">
                          {crumb.label}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      )}

      {/*
        Two clusters, not four loose buttons.

        Utilities (theme, notifications) read as icons; the two things that go
        somewhere are labelled. They used to alternate outline-ghost-ghost-
        outline, which gave the eye no grouping to hold on to, and the icon
        buttons were 32px against 28px labelled ones — a row that never quite
        lined up. Everything is 32px now, and the rule says where the change
        of kind is.
      */}
      <div className="ms-auto flex items-center gap-1">
        {status && <div className="me-3 flex items-center">{status}</div>}
        {/* A running trial says how long it has left (docs/pricing-v2.md). */}
        <TrialPill className="me-2 hidden sm:inline-flex" />
        {/* INT or Local: never shown on production. */}
        <EnvironmentBadge className="me-2" />
        {/* Who else is in this account right now. Renders nothing when you are alone. */}
        <PresenceStack />
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleColorMode}
          aria-label={isDarkMode ? (t('TOPBAR.THEME.LIGHT') as string) : (t('TOPBAR.THEME.DARK') as string)}
        >
          {isDarkMode ? <Sun /> : <Moon />}
        </Button>

        <Popover open={notifOpen} onOpenChange={(open) => (open ? handleNotifOpen() : setNotifOpen(false))}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" aria-label={t('TOPBAR.NOTIFICATIONS.TITLE') as string}>
              <Bell />
              {unreadCount > 0 && (
                /* Overridden for size and place only — never colour. A Badge
                   at its own h-5 would all but cover a 32px icon button. */
                <Badge className="absolute -end-0.5 -top-0.5 h-4 min-w-4 px-1 text-[10px] tabular-nums">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
              <span className="text-sm font-medium">{t('TOPBAR.NOTIFICATIONS.TITLE')}</span>
              <span className="flex items-center gap-1">
                {notifLoading && <Spinner className="size-4 text-muted-foreground" />}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('ARDI.NOTIFICATIONS.SETTINGS_TAB') as string}
                  onClick={() => {
                    setNotifOpen(false);
                    navigate(notificationsPath(!!isSuperAdmin, 'settings'));
                  }}
                >
                  <Settings2 />
                </Button>
              </span>
            </div>
            {notifItems.length ? (
              <NotificationList
                items={notifItems}
                onNavigate={(route) => {
                  setNotifOpen(false);
                  navigate(route);
                }}
              />
            ) : (
              /* Empty, not a centred paragraph: the same nothing-here shape
                 the rest of the app uses. Loading now shows in the header
                 above rather than as a line of text pretending to be news. */
              <Empty className="border-0 py-8">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <BellOff />
                  </EmptyMedia>
                  <EmptyTitle className="text-sm font-normal text-muted-foreground">
                    {t('TOPBAR.NOTIFICATIONS.EMPTY')}
                  </EmptyTitle>
                </EmptyHeader>
              </Empty>
            )}
            {showPushRow && (
              <div className="flex items-start gap-3 border-t px-4 py-3">
                <div className="min-w-0 flex-1">
                  <label htmlFor="push-notifications-switch" className="text-sm font-medium">
                    {t('PUSH.LABEL')}
                  </label>
                  {pushHint && <p className="text-xs leading-snug text-muted-foreground">{t(pushHint)}</p>}
                </div>
                <Switch
                  id="push-notifications-switch"
                  className="mt-0.5"
                  checked={push.status === 'on'}
                  disabled={push.busy || push.status === 'denied' || push.status === 'ios-install'}
                  onCheckedChange={(on) => void push.setEnabled(on)}
                />
              </div>
            )}
            <div className="border-t p-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center text-muted-foreground"
                onClick={() => {
                  setNotifOpen(false);
                  navigate(notificationsPath(!!isSuperAdmin));
                }}
              >
                {t('ARDI.NOTIFICATIONS.SEE_ALL')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Vendors also have the floating button, bottom right. */}
        {isSuperAdmin ? <StaffSupportLauncher /> : <SupportLauncher />}

        <Separator orientation="vertical" className="mx-1 h-5" />

        {askAi && (
          <Button variant="outline" onClick={() => askAi.open()}>
            <Sparkles data-icon="inline-start" />
            <span className="hidden sm:inline">{t('ASSISTANT.TITLE', 'Ask Ardi')}</span>
          </Button>
        )}

        {!isSuperAdmin && (
          <Button variant="outline" onClick={openVisualizer}>
            <ExternalLink data-icon="inline-start" />
            <span className="hidden sm:inline">{t('TOPBAR.MY_VISUALIZER')}</span>
          </Button>
        )}
      </div>

      {title && <span className="sr-only">{title}</span>}
    </header>
  );
}
